import type { ShareableField } from '@amzn/journeys-common';
import type {
  FlavorWithContent,
  MCCVarSharingConfig,
  ShareableFieldConfigWithDefaultSharing,
} from 'features/traffic/campaigns/campaign-configs/types';
import {
  extractLinkTypeAndValue,
  isLinkTypeAndValueString,
} from 'features/traffic/campaigns/content/common-components/RefTaggedLinkEditor/ref-tagged-link-utils';
import type { ContentFieldPath } from 'features/traffic/campaigns/content/shared-variables/shared-variables.types';
import {
  flavorsHaveSameLanguageCode,
  flavorsHaveSameMarketplace,
  isActiveFlavor,
} from 'features/traffic/campaigns/utils/flavor-filtration';
import type { FourPackCardShareableFieldsMetaMap, MobileFourPackCardContent } from '../types';

const fieldPathsMap: Record<keyof FourPackCardShareableFieldsMetaMap, ContentFieldPath> = {
  asin: 'content.panels[0].asin',
  // altText: 'content.altText',
  headline: 'content.headline',
  linkTypeAndLinkTypeValue: 'content.panels[0].destinationUrl.linkTypeAndLinkTypeValue',
  logoAltText: 'content.logoAltText',
  subHeadline: 'content.subHeadline',
};

type FlavorArgs = {
  otherFlavor: FlavorWithContent<MobileFourPackCardContent>;
  referenceFlavor: FlavorWithContent<MobileFourPackCardContent>;
  otherFlavorIndex: number;
  referenceFlavorIndex: number;
};

type MatchEligibleArgs = {
  flavor: FlavorWithContent<MobileFourPackCardContent>;
  testFlavor: FlavorWithContent<MobileFourPackCardContent>;
  flavorIndex: number;
  testFlavorIndex: number;
  testFlavorFieldValue?: unknown;
};

const checkIfMatchEligible = ({ flavor, testFlavor, flavorIndex, testFlavorIndex }: MatchEligibleArgs) => {
  if (flavorIndex === testFlavorIndex) return false;
  if (!isActiveFlavor(flavor) || !isActiveFlavor(testFlavor)) return false;
  if (!flavorsHaveSameLanguageCode(testFlavor, flavor) || !flavorsHaveSameMarketplace(testFlavor, flavor)) return false;
  return true;
};

const formatTextValue = (value: unknown): string => (value ? `${value}` : '""');

const formatLinkTypeAndValue = (value: unknown): string => {
  if (!isLinkTypeAndValueString(value)) return 'Unknown';
  const { linkType, linkTypeValue } = extractLinkTypeAndValue(value);
  return `${linkType} "${linkTypeValue}"`;
};

const createShareableField = (
  fieldKey: keyof FourPackCardShareableFieldsMetaMap,
  fieldKeyPath: keyof MobileFourPackCardContent,
  formatValueFn: (value: unknown) => string,
  linkCheck?: boolean,
): ShareableFieldConfigWithDefaultSharing<string, MobileFourPackCardContent> => ({
  checkIfMatchEligible,
  formatValue: formatValueFn,
  getContentPath: () => fieldPathsMap[fieldKey],
  getField: (flavor) => flavor.content[fieldKeyPath] as ShareableField<string> | undefined,
  getShareableFieldPaths: ({ otherFlavor, otherFlavorIndex, referenceFlavor, referenceFlavorIndex }: FlavorArgs) => {
    if (otherFlavorIndex === referenceFlavorIndex) return [];
    if (!isActiveFlavor(otherFlavor)) return [];
    if (!flavorsHaveSameLanguageCode(otherFlavor, referenceFlavor) || !flavorsHaveSameMarketplace(otherFlavor, referenceFlavor)) return [];
    return [
      `flavors[${otherFlavorIndex}].${fieldPathsMap[fieldKey]}`,
    ];
  },
  isDefaultShareable: true,
  valueFilterFn: (v): v is string => (linkCheck ? isLinkTypeAndValueString(v) : true),
});

// TODO: Update this in order to support dynamic panels for "n" pack card in future.
export const fourPackCardVarSharingConfig: MCCVarSharingConfig<FourPackCardShareableFieldsMetaMap, MobileFourPackCardContent> = {
  checkIfDefaultSharingIsSupported: ({ flavors }) => !flavors.some(({ active }) => active),
  defaultSharingEnabled: true,
  shareableFields: {
    asin: createShareableField('asin', 'panels[0].asin' as keyof MobileFourPackCardContent, formatTextValue),
    // altText: createShareableField('altText', 'altText', formatTextValue),
    headline: createShareableField('headline', 'headline', formatTextValue),
    linkTypeAndLinkTypeValue: createShareableField(
      'linkTypeAndLinkTypeValue',
      'panels[0].destinationUrl' as keyof MobileFourPackCardContent,
      formatLinkTypeAndValue,
      true,
    ),
    logoAltText: createShareableField('logoAltText', 'logoAltText', formatTextValue),
    subHeadline: createShareableField('subHeadline', 'subHeadline', formatTextValue),
  },
};
